<template>
  <div id="app" class="classic-book-reader" :class="{'no-scroll': isAnyAreaCollapsed}" v-loading="isLoading" loading-text="数据加载中...">
    <!-- 第1区域：页眉区 -->
    <header class="header" v-if="isForbidUser=='1'">
      <div class="header-container">
        <!-- Logo和网站名称 -->
        <div class="logo-area">
          <div class="logo" style="cursor: pointer;" @click="goFirstPage">
            <img width="60" height="60" src="../../assets/bookpic/newlogo.png" alt="Logo">
          </div>
          <div class="site-info">
            <!-- <h1 class="site-title">墨库·MoKu∞</h1>
            <p class="site-subtitle">文档档案智能检索、阅读与研究平台</p> -->
            <h1 class="site-title">蝠池墨庫</h1>
            <p class="site-subtitle">
              <span style="font-size:16px;display: inline-block;transform: scaleY(1) scaleX(0.8);transform-origin: center center;margin-left:-9px;">FuChiMoKu</span>
              <span style="font-size:12px;margin-left:-12px;">∞</span></p>
          </div>
        </div>

        <!-- 首页圆圈 -->
        <div 
          class="home-circle" 
          @click="goBackPage"
          :class="{'active': activeSection === 'home'}"
        >
          返回
        </div>

        <!-- 搜索框 -->
        <div class="search-area">
          <div class="search-box">
            <i class="el-icon-search search-icon"></i>
            <input
              v-model="searchKeyword"
              type="text"
              class="search-input"
              placeholder="请输入章节名、正文内容等关键词进行检索"
              @keyup.enter="handleSearch"
            />
            <button class="search-button" @click="handleSearch">搜索</button>
          </div>
        </div>
      </div>
    </header>

    <!-- 搜索弹出层 -->
    <div v-if="showSearchResults" class="search-results-overlay" @click.self="closeSearchResults">
      <div class="search-results-container">
        <div class="search-results-header">
          <h3 class="search-results-title">搜索结果</h3>
          <div class="search-results-close" @click="closeSearchResults">
            <i class="el-icon-close"></i>
          </div>
        </div>
        <div class="search-results-content">
          <div v-if="searchResults.length === 0" class="no-results">
            <i class="el-icon-search"></i>
            <p>未找到相关结果</p>
          </div>
          <div v-else class="search-results-list">
            <div 
              v-for="(result, index) in searchResults" 
              :key="index" 
              class="search-result-item"
              @click="goToSearchResult(result)"
            >
              <div class="result-header">
                <!-- <div class="result-title">{{ result.chapter }} - {{ result.section }}</div>     20260213 modify-->
                <div class="result-title" v-html="highlightKeyword(result.chapter + ' - ' + result.section)"></div>

                <div class="result-page">第{{ result.page }}页</div>
              </div>
              <div class="result-content" v-html="highlightKeyword(result.content)"></div>
            </div>
          </div>
        </div>
        <div class="search-results-footer">
          共找到 {{ searchResults.length }} 条结果
        </div>
      </div>
    </div>

    <!-- 主内容区域 -->
    <main class="main-container" :class="{
      'directory-collapsed': isDirectoryCollapsed, 
      'image-collapsed': isImageAreaCollapsed, 
      'text-collapsed': isTextAreaCollapsed
    }" v-if="isForbidUser=='1'">
      <!-- 第2区域：目录区 -->
      <section 
        class="directory-section" 
        :class="{'collapsed': isDirectoryCollapsed}"
      >
        <div class="section-header">
          <h3 class="section-title">目录</h3>
          <div 
            class="collapse-icon"
            @click="toggleDirectoryCollapse"
            v-show="!isImageAreaCollapsed && !isTextAreaCollapsed"
          >
            <!-- 第2区域箭头：默认左箭头，点击后变右箭头 -->
            <i :class="directoryArrowClass"></i>
          </div>
        </div>

        <!-- 一键展开/收起标签 -->
        <div class="expand-tabs" v-show="!isDirectoryCollapsed">
          <div 
            class="expand-tab"
            :class="{'active': isAllExpanded}"
            @click="expandAllChapters"
          >
            一键展开
          </div>
          <div 
            class="collapse-tab"
            :class="{'active': !isAllExpanded}"
            @click="collapseAllChapters"
          >
            一键收起
          </div>
        </div>

        <!-- 目录树 -->
        <div class="directory-tree" v-show="!isDirectoryCollapsed">
          <el-tree
            ref="directoryTree"
            :data="directoryData"
            :props="treeProps"
            :default-expand-all="isAllExpanded"
            :expand-on-click-node="false"
            node-key="id"
            :highlight-current="true"
            @node-click="handleDirectoryClick"
            class="book-tree"
          >
            <!-- 自定义树节点，添加标题提示 -->
            <span 
              class="custom-tree-node" 
              slot-scope="{ node, data }"
              :title="getNodeTooltip(node, data)"
            >
              <span class="tree-node-content">
                <i 
                  v-if="data.level === 1"
                  class="el-icon-folder-opened"
                ></i>
                <i 
                  v-else-if="data.level === 2"
                  class="el-icon-document"
                ></i>
                <span>{{ node.label }}</span>
              </span>
              <span v-if="data.page && data.level === 2" class="page-indicator">
                第{{ data.page }}页
              </span>
            </span>
          </el-tree>
        </div>
      </section>

      <!-- 第3区域：书籍原文图片区 -->
      <section 
        class="image-section" 
        :class="{'collapsed': isImageAreaCollapsed}"
      >
        <div class="section-header">
          <h3 class="section-title">原文影像</h3>
          <div 
            class="collapse-icon"
            @click="toggleImageAreaCollapse"
            v-show="!isDirectoryCollapsed && !isTextAreaCollapsed"
          >
            <!-- 第3区域箭头：默认左箭头，点击后变右箭头 -->
            <i :class="imageArrowClass"></i>
          </div>
        </div>

        <!-- 图片显示区 -->
        <div class="image-display-area" v-show="!isImageAreaCollapsed" :class="{'thumbnails-hidden': thumbnailsHidden}">
          <!-- 左侧竖排小图片 -->
          <div v-if="!thumbnailsHidden" class="thumbnail-sidebar">
            <!-- 小图片显示控制按钮 -->
            <div class="thumbnail-control">
              <div 
                class="control-button hide-thumbnails"
                @click="toggleThumbnails"
                :title="thumbnailsHidden ? '显示小图片' : '隐藏小图片'"
              >
                <i class="el-icon-close"></i>
              </div>
            </div>
            
            <!-- 向上箭头圆圈 -->
            <div 
              class="thumbnail-nav up-nav"
              :class="{'disabled': thumbnailStartIndex === 1}"
              @click="scrollThumbnailsUp"
            >
              <div class="nav-circle">
                <i class="el-icon-arrow-up"></i>
              </div>
            </div>
            
            <!-- 小图片列表 -->
            <div class="thumbnail-list">
              <div 
                v-for="(page, index) in visibleThumbnails" 
                :key="page"
                class="thumbnail-item"
                :class="{'active': page === currentPage}"
                @click="selectThumbnail(page)"
                :title="`第${page}页`"
              >
                <img 
                  :src="bookImages[page]" 
                  :alt="`第${page}页`"
                  class="thumbnail-image"
                />
                <!-- 上面img标签中，去掉了图片url不存在的异常处理代码：@error="handleImageError" -->

                <!-- 页码区域（隐藏但代码保留） -->
                <div class="thumbnail-label" style="display: none;">第{{ page }}页</div>
              </div>
            </div>
            
            <!-- 向下箭头圆圈 -->
            <div 
              class="thumbnail-nav down-nav"
              :class="{'disabled': thumbnailStartIndex + 5 >= totalPages}"
              @click="scrollThumbnailsDown"
            >
              <div class="nav-circle">
                <i class="el-icon-arrow-down"></i>
              </div>
            </div>
          </div>
          
          <!-- 小图片显示控制按钮（当小图片隐藏时显示） -->
          <div v-else class="show-thumbnails-button">
            <div 
              class="control-button show-thumbnails"
              @click="toggleThumbnails"
              title="显示小图片"
            >
              <i class="el-icon-arrow-right"></i>
            </div>
          </div>
          
          <!-- 主图片显示区 -->
          <div 
            class="image-container"
            ref="imageContainer"
            @mousedown="startDrag"
            @touchstart="startDrag"
          >
            <img 
              :src="currentBookImage" 
              :alt="currentPageTitle"
              :style="imageStyle"
              class="book-page-image"
              ref="bookImage"
            />
            <!-- 拖动提示 -->
            <div v-if="imageScale > 1 && isDragging" class="drag-hint">
              拖动中...
            </div>
          </div>
        </div>

        <!-- 图片控制区 -->
        <div class="image-control-area" v-show="!isImageAreaCollapsed">
          <!-- 分页控制 -->
          <div class="pagination-control">
            <div 
              class="control-button prev-page"
              :class="{'disabled': currentPage === 1}"
              @click="prevPage"
            >
              <i class="el-icon-arrow-left"></i>
            </div>
            <div class="page-info">
              <span class="current-page">{{ currentPage }}</span>
              <span class="separator">/</span>
              <span class="total-pages">{{ totalPages }}</span>
            </div>
            <div 
              class="control-button next-page"
              :class="{'disabled': currentPage === totalPages}"
              @click="nextPage"
            >
              <i class="el-icon-arrow-right"></i>
            </div>
            <div class="page-jump">
              <input
                v-model="jumpPage"
                type="number"
                class="jump-input"
                placeholder="页码"
                min="1"
                :max="totalPages"
                @keyup.enter="jumpToPage"
              />
              <button class="jump-button" @click="jumpToPage">跳转</button>
            </div>
          </div>

          <!-- 放大缩小区 -->
          <div class="zoom-control">
            <div 
              class="control-button zoom-out"
              :class="{'disabled': imageScale <= 0.5}"
              @click="zoomOut"
            >
              <i class="el-icon-zoom-out"></i>
            </div>
            <div class="zoom-info">
              {{ Math.round(imageScale * 100) }}%
            </div>

            <!-- 20260202，下面div中原值：imageScale >= 2，修改为图片可放大5倍 -->
            <div 
              class="control-button zoom-in"
              :class="{'disabled': imageScale >= 5}"
              @click="zoomIn"
            >
              <i class="el-icon-zoom-in"></i>
            </div>
          </div>

          <!-- 原比例区 -->
          <div class="original-control">
            <span class="original-text">原比例</span>
            <div 
              class="control-button reset-zoom"
              @click="resetZoomAndPosition"
            >
              <i class="el-icon-refresh"></i>
            </div>
          </div>
        </div>
      </section>

      <!-- 第4区域：书籍文字区 -->
      <section 
        class="text-section" 
        :class="{'collapsed': isTextAreaCollapsed}"
      >
        <div class="section-header">
          <h3 class="section-title">{{ currentChapterTitle }}</h3>
          <div 
            class="collapse-icon"
            @click="toggleTextAreaCollapse"
            v-show="!isDirectoryCollapsed && !isImageAreaCollapsed"
          >
            <!-- 第4区域箭头：默认右箭头，点击后变左箭头 -->
            <i :class="textArrowClass"></i>
          </div>
        </div>

        <!-- 文字内容 -->
        <div class="text-content-area" v-show="!isTextAreaCollapsed">
          <div class="chapter-content">
            <h4 class="section-subtitle">{{ currentSectionTitle }}</h4>
            <div class="book-text-content">
              <!-- 20260317，实现内容的换行，导入meilisearch的json文件中包括换行符<br> -->
              <!-- <p v-for="(paragraph, index) in currentBookText" :key="index">
                {{ paragraph }}
              </p> -->
              <p v-for="(paragraph, index) in currentBookText" :key="index" v-html="paragraph">
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- 移动端响应式导航 -->
    <div v-if="isMobile && (isForbidUser=='1')" class="mobile-navigation">
      <div 
        class="mobile-nav-item"
        :class="{'active': activeMobileTab === 'directory'}"
        @click="switchMobileTab('directory')"
      >
        <i class="el-icon-tickets"></i>
        <span>目录</span>
      </div>
      <div 
        class="mobile-nav-item"
        :class="{'active': activeMobileTab === 'image'}"
        @click="switchMobileTab('image')"
      >
        <i class="el-icon-picture-outline"></i>
        <span>影像</span>
      </div>
      <div 
        class="mobile-nav-item"
        :class="{'active': activeMobileTab === 'text'}"
        @click="switchMobileTab('text')"
      >
        <i class="el-icon-document"></i>
        <span>文字</span>
      </div>
    </div>

    <div align="center" style="margin-top:100px;cursor: pointer;" v-if="isForbidUser=='2'" @click="goBackPage"><u>账户被禁用，不可阅读。点击可返回</u></div>
  </div>
</template>

<script>
import {getChapterpagelist, getBookpagelist, getBookpagecontent, getTradChineseOfSimple} from '../../api/home'
import { developmentUrl } from "../../config/env";

export default {
  name: 'ClassicBookReader',
  data() {
    return {
      // 搜索相关
      searchKeyword: '',
      searchKeywordTrad : '',
      activeSection: 'home',
      showSearchResults: false,
      searchResults: [],
      
      // 搜索测试数据
      /*
      searchTestData: [
        {
          chapter: '第1章 赴任谢恩疏',
          section: '第1页',
          page: 1,
          content: '《边奏存稿》是明代官员叶盛所著《叶文庄公奏疏》中的专项奏议集之一，共七卷，主要收录其在山西任职期间呈递的边关防务奏议。作为叶盛政务实践的集中体现，该卷内容聚焦于明代中叶的北疆治理。'
        },
        {
          chapter: '第1章 赴任谢恩疏',
          section: '第2页',
          page: 2,
          content: '《边奏存稿》是叶盛奏疏的专项汇编，收录于其子叶淇编纂的《文庄集》中，后析出为《叶文庄奏疏》四十卷的一部分。现存版本为崇祯四年（1631年）叶氏赐书楼刊本，被《四库全书总目提要》收入史部诏令奏议类。'
        }
      ],
      */
      searchTestData: [],
      
      // 区域折叠状态
      isDirectoryCollapsed: false,
      isImageAreaCollapsed: false,
      isTextAreaCollapsed: false,
      
      // 小图片显示状态
      thumbnailsHidden: false,
      
      // 目录展开状态
      isAllExpanded: false,
      
      // 书籍数据
      currentPage: 1,
      totalPages: 1,
      jumpPage: '',
      imageScale: 1,
      imagePosition: { x: 0, y: 0 },
      isDragging: false,
      dragStart: { x: 0, y: 0 },
      
      // 小图片相关
      thumbnailStartIndex: 1,
      thumbnailCount: 6,
      
      // 当前内容
      currentBookImage: 'https://picsum.photos/600/800?random=1&grayscale',
      currentChapterTitle: '第1章',
      currentSectionTitle: '第1页',
      currentBookText: [
        '此页无正文内容。',
      ],
      defaultImage: '../../assets/uploadwill.jpg',
      
      // directoryData: [
      //   {
      //     id: 1,
      //     label: '第1章：赴任谢恩疏',
      //     level: 1,
      //     page: 1,
      //     expanded: true,
      //     children: [
      //       { id: 11, label: '', level: 2, page: 1 },
      //       { id: 12, label: '', level: 2, page: 2 },
      //       { id: 13, label: '', level: 2, page: 3 },
      //       { id: 14, label: '', level: 2, page: 4 },
      //       { id: 15, label: '', level: 2, page: 5 },
      //       { id: 16, label: '', level: 2, page: 6 }
      //     ]
      //   },
      // ],
      directoryData: [],    //菜单树目录数据
      bookImages: {},       //界面上主图片url数据
      haveGrantArr: [],     //有无阅读权限的标记
      everyChapterOnePage: [],  //每个章节的第一个页码

      treeProps: {
        children: 'children',
        label: 'label'
      },
      
      // 移动端相关
      isMobile: false,
      activeMobileTab: 'image',
      windowWidth: 0,
      isLoading: false,
      isForbidUser: '1',    //是否被禁用的用户,1:未禁用；2：禁用
      isVipUser: 0,   //是否vip用户，0：访客，1：Vip用户
      pictureStoreUrlFix: '',
    };
  },
  computed: {
    imageStyle() {
      return {
        transform: `scale(${this.imageScale}) translate(${this.imagePosition.x}px, ${this.imagePosition.y}px)`,
        transformOrigin: 'center center',
        transition: this.isDragging ? 'none' : 'transform 0.3s ease',
        cursor: this.imageScale > 1 ? 'grab' : 'default'
      };
    },
    
    // 判断是否有区域被折叠
    isAnyAreaCollapsed() {
      return this.isDirectoryCollapsed || this.isImageAreaCollapsed || this.isTextAreaCollapsed;
    },
    
    // 第2区域箭头类
    directoryArrowClass() {
      // 20260201,点击箭头后箭头可以变方向
      // return this.isDirectoryCollapsed ? 'el-icon-arrow-right' : 'el-icon-arrow-left';
      return 'el-icon-arrow-left';
    },
    
    // 第3区域箭头类
    imageArrowClass() {
      // 20260201,点击箭头后箭头可以变方向
      // return this.isImageAreaCollapsed ? 'el-icon-arrow-right' : 'el-icon-arrow-left';
      return 'el-icon-arrow-left';
    },
    
    // 第4区域箭头类
    textArrowClass() {
      // 20260201,点击箭头后箭头可以变方向
      // return this.isTextAreaCollapsed ? 'el-icon-arrow-left' : 'el-icon-arrow-right';
      return 'el-icon-arrow-right';
    },
    
    // 可见的小图片
    visibleThumbnails() {
      const thumbnails = [];
      for (let i = 0; i < this.thumbnailCount; i++) {
        const page = this.thumbnailStartIndex + i;
        if (page <= this.totalPages) {
          thumbnails.push(page);
        }
      }
      return thumbnails;
    },
    
    // 图片库。测试性质的随机图片库
    /*
    bookImages() {
      const images = {};   //注意此处不是数组，是map对象
      for (let i = 1; i <= this.totalPages; i++) {
        images[i] = `https://picsum.photos/600/800?random=${i}&grayscale`;
      }
      return images;
    }
    */
  },
  watch: {
    currentPage(newPage) {
      this.updateContentByPage(newPage);
      this.updateThumbnails(newPage);
    },
    
    activeMobileTab(newTab) {
      // 移动端标签切换时显示对应区域
      this.isDirectoryCollapsed = newTab !== 'directory';
      this.isImageAreaCollapsed = newTab !== 'image';
      this.isTextAreaCollapsed = newTab !== 'text';
    }
  },
  mounted() {
    this.checkIfMobile();
    window.addEventListener('resize', this.checkIfMobile);
    
    // 添加鼠标和触摸事件监听
    document.addEventListener('mousemove', this.handleDrag);
    document.addEventListener('mouseup', this.stopDrag);
    document.addEventListener('touchmove', this.handleDrag, { passive: false });
    document.addEventListener('touchend', this.stopDrag);
    
    // 初始化内容
    this.updateContentByPage(this.currentPage);
    this.updateThumbnails(this.currentPage);

    //20260210,跳转到搜索关键字所在的页码********************begin
    if(this.$route.query.pagenoint != undefined){
      this.$message({
        message: `正在跳转您搜索关键字所在页码的正文，请稍等！`,
        type: 'success',
        customClass: 'search-message'
      });

      setTimeout(() => {
        this.currentPage = this.$route.query.pagenoint;
        // 根据页码，从章节目录树的数据中获取当前章节名称
        this.getCharterNameOfTree(this.currentPage);
      }, 1000);
    }
    //20260210,跳转到搜索关键字所在的页码********************end

  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkIfMobile);
    document.removeEventListener('mousemove', this.handleDrag);
    document.removeEventListener('mouseup', this.stopDrag);
    document.removeEventListener('touchmove', this.handleDrag);
    document.removeEventListener('touchend', this.stopDrag);
  },

  //初始化获取网络数据
  created() {
    this.isForbidUser = localStorage.getItem("isForbidUser");

    //20260316,获取用户的vip身份和本地图库的网址链接前缀
    if (localStorage.getItem("isVipUser") !== null) {
      this.isVipUser = localStorage.getItem("isVipUser");
      if(this.isVipUser == 1){
        this.pictureStoreUrlFix = localStorage.getItem("pictureStoreUrlFix");
      }
    } else {
      this.isVipUser = 0; //非vip用户
    }

    if(this.isVipUser == 0){
      this.currentBookText = ['无查看权限。'];
    }

    this.isLoading = true;
    if(this.$route.query.twotype !=0){   //有分册的系列图书
      this.GetChapterpagelist();
    }
    else{   //无分册的系列图书。20260209,统一了有分册和无分册的书籍数据表结构，所以此条件代码不执行
      this.GetBookpagelist();
    }
  },

  methods: {
    //获取系列图书（有分册）章节和页码结构数据,20260209,统一了有分册和无分册的书籍数据表结构，所以此方法有分册和无分册都满足
    GetChapterpagelist(){
      this.directoryData = [];
      this.bookImages = {};

      let queryParams = {};
      queryParams.bookid = this.$route.query.twotype;  //书籍分册的标识
      queryParams.remark = this.$route.query.type;     //书籍系列总册的标识

      //20260315 add,获取用户的身份类型，传入后台服务进行书籍权限的处理
      queryParams.isvipuser = this.isVipUser;
      //20260402,传入用户的hash值到后端，进行vip的判断
      if (localStorage.getItem("UserOfPhoneHash") !== null){
        queryParams.chaptername = localStorage.getItem("UserOfPhoneHash");
      }
      else{
        queryParams.chaptername = "0";
      }
      // console.log("chartername===" + queryParams.chaptername);

      getChapterpagelist(queryParams).then(response => {
        this.directoryData = response.data.rows;
        this.totalPages = response.data.maprowdata.totalPages;

        //形成界面主图片的map对象。此处为测试性质的图片map对象建立。正式的图片map对象访问电脑端“本地软件”
        let picNo = 0;
        this.everyChapterOnePage = [];
        for(let i=0;i<this.directoryData.length;i++){
          let subArray = [];

          // 测试方式：目前真实图片不足，所以各章节采用第一章节的图片
          // subArray = this.directoryData[0].children;
          // 正式方式
          subArray = this.directoryData[i].children;

          // 初始化刚进入阅读页的章节标题和页码，20260205
          if(i==0){
            this.currentChapterTitle = this.directoryData[i].label;
            this.currentSectionTitle = '第' + this.directoryData[i].page + '页';
          }

          // 将每个章节的第一个页码存入数组中
          this.everyChapterOnePage.push(this.directoryData[i].page);

          for(let j=0;j<subArray.length;j++){
            if(i==0 && j==0){
              // 20250316 modify
              // this.currentBookImage = developmentUrl + 'profile/testbookpic/' + subArray[j].pageall + ".jpg";   //初始化第一张图片
              if(subArray[j].pageall == 'nograntpicture'){
                this.currentBookImage = developmentUrl + 'profile/testbookpic/' + subArray[j].pageall + ".jpg";
              }
              else{
                this.currentBookImage = this.pictureStoreUrlFix + subArray[j].pageall + ".jpg";
              }
            }
            picNo ++ ;

            // 测试方式：目前真实图片不足，所以采用下标求余的模式，进行用户演示，后面需要的正式方法：为检测图片是否存在，如不存在，则写入“默认提示图片”
            /*
            let jreal = j % 10;
            this.bookImages[picNo]= developmentUrl + 'profile/testbookpic/' + subArray[jreal].pageall + ".jpg";
            if(subArray[jreal].pageall == 'nograntpicture'){
              this.haveGrantArr[picNo-1] = 0;
            }
            else{
              this.haveGrantArr[picNo-1] = 1;
            }
            */

            // 正式方式
            if(subArray[j].pageall == 'nograntpicture'){
              this.haveGrantArr[picNo-1] = 0;    //当前页无权限
              this.bookImages[picNo]= developmentUrl + 'profile/testbookpic/' + subArray[j].pageall + ".jpg";
            }
            else{
              this.haveGrantArr[picNo-1] = 1;    //当前页有权限
              this.bookImages[picNo]= this.pictureStoreUrlFix + subArray[j].pageall + ".jpg";
              // console.log("图库的图片的url===" + this.bookImages[picNo]);
            }
          }
        }

        this.isLoading = false;
      })
    },


    //获取系列图书（无分册）章节和页码结构数据。20260209,统一了有分册和无分册的书籍数据表结构，所以此区间方法本版本不用*************************begin
    GetBookpagelist(){
      this.directoryData = [];
      this.bookImages = {};

      let queryParams = {};
      queryParams.id = this.$route.query.type;  //书籍分册的标识
      getBookpagelist(queryParams).then(response => {
        this.directoryData = response.data.rows;
        this.totalPages = response.data.maprowdata.totalPages;

        //形成界面主图片的map对象。此处为测试性质的图片map对象建立。正式的图片map对象访问电脑端“本地软件”
        for(let i=0;i<this.directoryData.length;i++){
          // 初始化刚进入阅读页的章节标题和页码，20260205
          if(i==0){
            this.currentChapterTitle = this.directoryData[i].label;
            this.currentSectionTitle = '第' + this.directoryData[i].page + '页';
          }

          let subArray = [];
          subArray = this.directoryData[i].children;
          for(let j=0;j<subArray.length;j++){
            if(i==0 && j==0){
              this.currentBookImage = developmentUrl + 'profile/testbookpic/' + subArray[j].pageall + ".jpg";   //初始化第一张图片
            }
            picNo ++ ;

            // 正式方式
            /*
            this.bookImages[picNo]= developmentUrl + 'profile/testbookpic/' + subArray[j].pageall + ".jpg";
            if(subArray[j].pageall == 'nograntpicture'){
              this.haveGrantArr[picNo-1] = 0;    //当前页有权限
            }
            else{
              this.haveGrantArr[picNo-1] = 1;    //当前页无权限
            }
            */

            //测试方式：目前真实图标不足，所以采用下标求余的模式，进行用户演示，后面需要的正式方法：为检测图片是否存在，如不存在，则写入“默认提示图片”
            let jreal = j % 10;
            this.bookImages[picNo]= developmentUrl + 'profile/testbookpic/' + subArray[jreal].pageall + ".jpg";
            if(subArray[jreal].pageall == 'nograntpicture'){
              this.haveGrantArr[picNo-1] = 0;
            }
            else{
              this.haveGrantArr[picNo-1] = 1;
            }

          }
        }

        this.isLoading = false;
      })
    },
    //获取系列图书（无分册）章节和页码结构数据。20260209,统一了有分册和无分册的书籍数据表结构，所以此区间方法本版本不用*************************end


    // 获取树节点提示文字
    getNodeTooltip(node, data) {
      if (data.level === 1) {
        return `${data.label} - 第${data.page}页`;
      } else if (data.level === 2) {
        // 查找父级菜单
        const parentNode = node.parent;
        if (parentNode && parentNode.data) {
          // return `${parentNode.data.label} - ${data.label} - 第${data.page}页`;
          return `${parentNode.data.label} - 第${data.page}页`;
        }
        return `${data.label} - 第${data.page}页`;
      }
      return data.label;
    },
    
    checkIfMobile() {
      this.windowWidth = window.innerWidth;
      this.isMobile = this.windowWidth <= 768;
      
      if (this.isMobile) {
        // 移动端默认只显示图片区域
        this.isDirectoryCollapsed = true;
        this.isImageAreaCollapsed = false;
        this.isTextAreaCollapsed = true;
        this.activeMobileTab = 'image';
        this.thumbnailsHidden = true; // 移动端默认隐藏小图片
      } else {
        // PC端显示所有区域
        this.isDirectoryCollapsed = false;
        this.isImageAreaCollapsed = false;
        this.isTextAreaCollapsed = false;
        this.thumbnailsHidden = false; // PC端默认显示小图片
      }
    },
    
    handleSearch() {
      if (!this.searchKeyword.trim()) {
        this.$message({
          message: '请输入搜索关键词',
          type: 'warning',
          customClass: 'search-message'
        });
        return;
      }
      
      // 模拟搜索逻辑
      const keyword = this.searchKeyword.toLowerCase();
      //屏蔽不能搜索的关键字
      if(keyword == '第' || keyword == '页' || keyword == '章' 
      || keyword == ',' || keyword == '，' || keyword == '；'|| keyword == '"' || keyword == "'" || keyword == '‘' || keyword == '’' 
      || keyword == '。' || keyword == '.' || keyword == ':' || keyword == ',' || keyword == '：'|| keyword == '”' || keyword == '“')
      {      
        this.searchResults = [];

        // 显示搜索结果
        this.showSearchResults = true;
        this.$message({
          message: `找到 ${this.searchResults.length} 条结果`,
          type: 'success',
          customClass: 'search-message'
        });
      }
      else{
        let msearchResults = [];
        
        let queryParams = {};
        queryParams.content = keyword;
        //根据简体搜索关键字获取繁体搜索关键字，再进行简繁体的复合搜索功能
        getTradChineseOfSimple(queryParams).then(response => {
          let textList = [];
          textList= response.data.rows;
          let keywordTrad = textList[0];
          this.searchKeywordTrad = keywordTrad;

          if(keywordTrad == ''){
            msearchResults = this.searchTestData.filter(item => {
              return item.chapter.toLowerCase().includes(keyword) || 
                     item.content.toLowerCase().includes(keyword);
            });
          }
          else{
            msearchResults = this.searchTestData.filter(item => {
              return item.chapter.toLowerCase().includes(keyword) || 
                     item.content.toLowerCase().includes(keyword) ||
                     item.chapter.toLowerCase().includes(keywordTrad) || 
                     item.content.toLowerCase().includes(keywordTrad);
            });
          }

          this.searchResults = [];
          for(let i=0;i<msearchResults.length;i++){
            let onemap = {};
            onemap = msearchResults[i];
            if(onemap.content.toLowerCase().includes(keyword)){
              this.searchResults.push(onemap);
            }
            else if(onemap.content.toLowerCase().includes(keywordTrad)){
              this.searchResults.push(onemap);
            }
            else{
              // 如果搜索关键字没在内容页只在章节名中，那么只将章节的第一页码作为搜索内容显示
              if((this.everyChapterOnePage.indexOf(onemap.page) >= 0)){
                this.searchResults.push(onemap);
              }
            }
          }

          // 显示搜索结果
          this.showSearchResults = true;
          this.$message({
            message: `找到 ${this.searchResults.length} 条结果`,
            type: 'success',
            customClass: 'search-message'
          });
        })
      }
    },

    // 仅支持简体汉字搜索简体汉字的功能，备份备用代码*************begin
    handleSearchSimple() {
      if (!this.searchKeyword.trim()) {
        this.$message({
          message: '请输入搜索关键词',
          type: 'warning',
          customClass: 'search-message'
        });
        return;
      }
      
      // 模拟搜索逻辑
      const keyword = this.searchKeyword.toLowerCase();
      //屏蔽不能搜索的关键字
      if(keyword == '第' || keyword == '页' || keyword == '章' 
      || keyword == ',' || keyword == '，' || keyword == '；'|| keyword == '"' || keyword == "'" || keyword == '‘' || keyword == '’' 
      || keyword == '。' || keyword == '.' || keyword == ':' || keyword == ',' || keyword == '：'|| keyword == '”' || keyword == '“')
      {      
        this.searchResults = [];
      }
      else{
        let msearchResults = [];
        msearchResults = this.searchTestData.filter(item => {
          return item.chapter.toLowerCase().includes(keyword) || 
                item.content.toLowerCase().includes(keyword);
        });
        this.searchResults = [];
        for(let i=0;i<msearchResults.length;i++){
          let onemap = {};
          onemap = msearchResults[i];
          if(onemap.content.toLowerCase().includes(keyword)){
            this.searchResults.push(onemap);
          }
          else{
            // 如果搜索关键字没在内容页只在章节名中，那么只将章节的第一页码作为搜索内容显示
            // console.log(this.everyChapterOnePage);
            // console.log(onemap.page);
            if((this.everyChapterOnePage.indexOf(onemap.page) >= 0)){
              this.searchResults.push(onemap);
            }
          }
        }
      }

      // 显示搜索结果
      this.showSearchResults = true;
      this.$message({
        message: `找到 ${this.searchResults.length} 条结果`,
        type: 'success',
        customClass: 'search-message'
      });
    },

    // 高亮显示关键词
    highlightKeywordSimple(text) {
      if (!this.searchKeyword) return text;

      const keyword = this.searchKeyword;
      const regex = new RegExp(`(${keyword})`, 'gi');
      // 20260202,此处采用class样式不起作用，需要将样式用style写才起作用
      // return text.replace(regex, '<span class="highlight">$1</span>');
      return text.replace(regex, '<span style="color:#ff0000;font-weight:bold;">$1</span>');
    },
    // 仅支持简体汉字搜索简体汉字的功能，备份备用代码*************end
    
    // 高亮显示关键词
    highlightKeyword(text) {
      if (!this.searchKeyword) return text;

      //20260317,去除其中的回车换行和空格符
      const regexSub = new RegExp('<br>&nbsp;&nbsp;', 'gi');
      text = text.replace(regexSub, '');

      if(this.searchKeywordTrad){
        const keywordTrad = this.searchKeywordTrad;
        const regexTrad = new RegExp(`(${keywordTrad})`, 'gi');
        text = text.replace(regexTrad, '<span style="color:#ff0000;font-weight:bold;">$1</span>');
      }

      const keyword = this.searchKeyword;
      const regex = new RegExp(`(${keyword})`, 'gi');
      return text.replace(regex, '<span style="color:#ff0000;font-weight:bold;">$1</span>');
    },
    
    // 跳转到搜索结果
    goToSearchResult(result) {
      this.currentPage = result.page;
      this.currentChapterTitle = result.chapter;
      this.currentSectionTitle = result.section;
      this.updateContentByPage(result.page);
      this.closeSearchResults();
      
      this.$message({
        message: `跳转到第${result.page}页`,
        type: 'success',
        customClass: 'search-message'
      });
    },
    
    // 关闭搜索结果
    closeSearchResults() {
      this.showSearchResults = false;
    },
    
    goBackPage() {
      this.activeSection = 'home';
      this.currentPage = 1;
      this.$router.go(-1);
    },
    
    toggleDirectoryCollapse() {
      this.isDirectoryCollapsed = !this.isDirectoryCollapsed;
      
      // 当一个区域展开时，其他区域必须恢复
      if (!this.isDirectoryCollapsed) {
        this.isImageAreaCollapsed = false;
        this.isTextAreaCollapsed = false;
      }
    },
    
    toggleImageAreaCollapse() {
      this.isImageAreaCollapsed = !this.isImageAreaCollapsed;
      
      // 当一个区域展开时，其他区域必须恢复
      if (!this.isImageAreaCollapsed) {
        this.isDirectoryCollapsed = false;
        this.isTextAreaCollapsed = false;
      }
    },
    
    toggleTextAreaCollapse() {
      this.isTextAreaCollapsed = !this.isTextAreaCollapsed;
      
      // 当一个区域展开时，其他区域必须恢复
      if (!this.isTextAreaCollapsed) {
        this.isDirectoryCollapsed = false;
        this.isImageAreaCollapsed = false;
      }
    },
    
    // 切换小图片显示/隐藏
    toggleThumbnails() {
      this.thumbnailsHidden = !this.thumbnailsHidden;
    },
    
    expandAllChapters() {
      this.isAllExpanded = true;
      this.$refs.directoryTree.store.nodesMap && Object.keys(this.$refs.directoryTree.store.nodesMap).forEach(key => {
        const node = this.$refs.directoryTree.store.nodesMap[key];
        if (node.childNodes && node.childNodes.length > 0) {
          node.expanded = true;
        }
      });
      
      // 延迟滚动到底部，确保目录树完全展开
      // 下面的延迟滚动代码，导致能看到第一个菜单的时候，点击一键展开，此时菜单会下滑中间。所以注释下面延迟的代码
      /*
      setTimeout(() => {
        this.scrollToBottom('.directory-tree');
      }, 100);
      */
    },
    
    collapseAllChapters() {
      this.isAllExpanded = false;
      this.$refs.directoryTree.store.nodesMap && Object.keys(this.$refs.directoryTree.store.nodesMap).forEach(key => {
        const node = this.$refs.directoryTree.store.nodesMap[key];
        if (node.childNodes && node.childNodes.length > 0) {
          node.expanded = false;
        }
      });
    },
    
    handleDirectoryClick(data) {
      if (data.page) {
        this.currentPage = data.page;
        
        // 查找章节标题
        for (const chapter of this.directoryData) {
          /*20260205 modify
          if (chapter.id === data.parentId || (chapter.children && chapter.children.some(child => child.id === data.id))) {   //点击的为第几页
            this.currentChapterTitle = chapter.label;
            break;
          }
          */
          if(data.level == 1){   //点击为章节标题
            if(this.currentPage == chapter.page){    //并且章节页码和当前的页码一致
              this.currentChapterTitle = chapter.label;
            }
          }
          else if (chapter.id === data.parentId || (chapter.children && chapter.children.some(child => child.id === data.id))) {   //点击的为第几页
            this.currentChapterTitle = chapter.label;
            break;
          }
        }
        // this.currentSectionTitle = data.label;    //20250205 modify
        this.currentSectionTitle = '第' + data.page + '页';   
        this.updateContentByPage(data.page);
      }
    },
    
    updateContentByPage(page) {
      this.currentBookImage = this.bookImages[page];
      
      /*
      const textMap = {
        1: [
          '《边奏存稿》是明代官员叶盛所著《叶文庄公奏疏》中的专项奏议集之一，共七卷，主要收录其在山西任职期间呈递的边关防务奏议。',
          '作为叶盛政务实践的集中体现，该卷内容聚焦于明代中叶的北疆治理。'
        ],
        2: [
          '《边奏存稿》是叶盛奏疏的专项汇编，收录于其子叶淇编纂的《文庄集》中，后析出为《叶文庄奏疏》四十卷的一部分。',
          '现存版本为崇祯四年（1631年）叶氏赐书楼刊本，被《四库全书总目提要》收入史部诏令奏议类。'
        ]
      };
      this.currentBookText = textMap[page] || [
        '此页为古籍原文，文字古朴典雅，意蕴深远。','读者宜静心研读，细细品味，方能领会古人深意。','字里行间，皆可见先贤智慧，道德文章，流传千古。'];
      */
      let queryParams = {};
      queryParams.id = this.$route.query.type;  //系列书籍的标识
      queryParams.booknums = this.$route.query.twotype;  //分册的标识，如果为无分册的书籍，则值为0
      getBookpagecontent(queryParams).then(response => {
        let listmap = response.data.rows;
        // const textMap = response.data.maprowdata;
        let textMap = {};
        this.searchTestData = [];
        for(let i=0;i<listmap.length;i++){
          let midMap = {};
          midMap = listmap[i];

          //形成页码和该页码正文内容的对象
          let arrStr = [];
          if(this.haveGrantArr[i] == 1){
            arrStr.push(midMap.pageContent);
          }
          else{
            arrStr.push('无查看权限。');
          }
          
          let key = parseInt(midMap.pageNo);
          textMap[key] = arrStr;

          //形成搜索的数据对象
          let oneSearch = {};
          oneSearch.chapter = midMap.chapter;
          oneSearch.section = '第' + midMap.pageNo + '页';
          oneSearch.page = parseInt(midMap.pageNo);
          oneSearch.content = midMap.pageContent;
          this.searchTestData.push(oneSearch);
        }
        
        this.currentBookText = textMap[page] || ['此页无正文内容。'];
        if(this.isVipUser == 0){
          this.currentBookText = textMap[page] || ['无查看权限。'];
        }

      })
    },
    
    // 更新小图片显示
    updateThumbnails(page) {
      // 确保当前页码在小图片范围内
      if (page < this.thumbnailStartIndex) {
        this.thumbnailStartIndex = Math.max(1, page - Math.floor(this.thumbnailCount / 2));
      } else if (page > this.thumbnailStartIndex + this.thumbnailCount - 1) {
        this.thumbnailStartIndex = Math.min(
          this.totalPages - this.thumbnailCount + 1,
          page - Math.floor(this.thumbnailCount / 2)
        );
      }
    },
    
    // 选择小图片
    selectThumbnail(page) {
      this.currentPage = page;

      // 根据页码，从章节目录树的数据中获取当前章节名称
      this.getCharterNameOfTree(this.currentPage);
    },
    
    // 向上滚动小图片
    scrollThumbnailsUp() {
      if (this.thumbnailStartIndex > 1) {
        this.thumbnailStartIndex = Math.max(1, this.thumbnailStartIndex - 1);
      }
    },
    
    // 向下滚动小图片
    scrollThumbnailsDown() {
      if (this.thumbnailStartIndex + this.thumbnailCount <= this.totalPages) {
        this.thumbnailStartIndex = Math.min(
          this.totalPages - this.thumbnailCount + 1,
          this.thumbnailStartIndex + 1
        );
      }
    },
    
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        // 根据页码，从章节目录树的数据中获取当前章节名称
        this.getCharterNameOfTree(this.currentPage);
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;

        // 根据页码，从章节目录树的数据中获取当前章节名称
        this.getCharterNameOfTree(this.currentPage);
      }
    },
    
    jumpToPage() {
      const page = parseInt(this.jumpPage);
      if (!isNaN(page) && page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.jumpPage = '';

        // 设置界面右侧的章节名称和页码
        // 根据meilisearch中数据获取章节名和页码方案存在问题，如果meilisearch中数据不全，不能找到正确的章节和页码
        /*
        const oneMap = this.searchTestData.find(item => item.page === page);
        this.currentChapterTitle = oneMap.chapter;
        this.currentSectionTitle = oneMap.section;
        */
        // 根据页码，从章节目录树的数据中获取当前章节名称
        this.getCharterNameOfTree(page);

      } else {
        this.$message.error(`请输入1-${this.totalPages}之间的页码`);
      }
    },

    //根据页码，从章节目录树的数据中设置当前章节名称和页码名称
    getCharterNameOfTree(pageNo){
      this.currentSectionTitle = '第' + pageNo + '页';
      for (const onedata of this.directoryData) {
        let chapterName = onedata.label;
        const oneMap = onedata.children.find(item => item.page == pageNo);
        if(oneMap){
          this.currentChapterTitle = chapterName;
          break;
        }
      }
    },
    
    zoomIn() {
      // 20260202，修改为图片可放大5倍
      /*
      if (this.imageScale < 2) {
        this.imageScale = Math.min(2, this.imageScale + 0.1);
        this.resetPosition();
      }
      */
      if (this.imageScale < 5) {
        this.imageScale = Math.min(5, this.imageScale + 0.1);
        this.resetPosition();
      }
    },
    
    zoomOut() {
      if (this.imageScale > 0.5) {
        this.imageScale = Math.max(0.5, this.imageScale - 0.1);
        this.resetPosition();
      }
    },
    
    resetZoomAndPosition() {
      this.imageScale = 1;
      this.resetPosition();
    },
    
    resetPosition() {
      this.imagePosition = { x: 0, y: 0 };
    },
    
    // 开始拖动
    startDrag(event) {
      if (this.imageScale <= 1) return;
      
      event.preventDefault();
      this.isDragging = true;
      
      if (event.type === 'mousedown') {
        this.dragStart = {
          x: event.clientX - this.imagePosition.x,
          y: event.clientY - this.imagePosition.y
        };
      } else if (event.type === 'touchstart') {
        const touch = event.touches[0];
        this.dragStart = {
          x: touch.clientX - this.imagePosition.x,
          y: touch.clientY - this.imagePosition.y
        };
      }
    },
    
    // 处理拖动
    handleDrag(event) {
      if (!this.isDragging) return;
      
      event.preventDefault();
      
      let clientX, clientY;
      if (event.type === 'mousemove') {
        clientX = event.clientX;
        clientY = event.clientY;
      } else if (event.type === 'touchmove') {
        const touch = event.touches[0];
        clientX = touch.clientX;
        clientY = touch.clientY;
      }
      
      // 计算新的位置
      const newX = clientX - this.dragStart.x;
      const newY = clientY - this.dragStart.y;
      
      // 限制拖动范围
      const container = this.$refs.imageContainer;
      if (container) {
        const containerWidth = container.offsetWidth;
        const containerHeight = container.offsetHeight;
        
        // 假设图片原始尺寸为600x800
        const imageWidth = 600 * this.imageScale;
        const imageHeight = 800 * this.imageScale;
        
        const maxX = Math.max(0, (imageWidth - containerWidth) / 2);
        const maxY = Math.max(0, (imageHeight - containerHeight) / 2);
        const minX = -maxX;
        const minY = -maxY;
        
        this.imagePosition.x = Math.max(minX, Math.min(maxX, newX));
        this.imagePosition.y = Math.max(minY, Math.min(maxY, newY));
      }
    },
    
    // 停止拖动
    stopDrag() {
      this.isDragging = false;
    },
    
    switchMobileTab(tab) {
      this.activeMobileTab = tab;
    },
    
    // 滚动到底部
    scrollToBottom(selector) {
      const element = document.querySelector(selector);
      if (element) {
        setTimeout(() => {
          element.scrollTop = element.scrollHeight;
        }, 50);
      }
    },

    // 返回首页界面
    goFirstPage(){
      //20260323
      // this.$router.push({path:'/'});
      if(this.isMobile){
        this.$router.push({path:'/'})
      }
      else{
        if (localStorage.getItem("inputTkNetUrl") !== null){
          window.location.href = localStorage.getItem("inputTkNetUrl");
        }
        else{
          this.$router.push({path:'/'})
        }
      }

    },

    // 图片不存在的默认提示图片处理
    handleImageError(event){
      this.$message.error(`您电脑上的图库软件异常了，请重新启动`);
      event.target.onerror = null; // 防止递归错误
    }

  }
};
</script>

<style lang="scss" scoped>
.classic-book-reader {
  width: 100%;
  height: 100vh;
  font-family: "Microsoft YaHei", "SimSun", "STKaiti", serif;
  display: flex;
  flex-direction: column;
  background-color: #f9f5f0;
  overflow: hidden;
  position: relative;
  
  &.no-scroll {
    overflow: hidden;
  }
  
  /* 第1区域：页眉区 */
  .header {
    width: 100%;
    height: 88px;
    background-color: #8B4513;
    color: #F5DEB3;
    border-bottom: 4px solid #D2691E;
    box-shadow: 0 2px 8px rgba(139, 69, 19, 0.3);
    flex-shrink: 0;
    z-index: 100;
    
    .header-container {
      width: 100%;
      height: 100%;
      padding: 0 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: relative;
      
      @media (max-width: 768px) {
        padding: 0 15px;
        flex-wrap: wrap;
        height: auto;
        padding: 10px 15px;
      }
      
      .logo-area {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        min-width: 280px;
        
        .logo {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #D2691E, #8B4513);
          /*border-radius: 50%;*/
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 15px;
          border: 2px solid #D2691E;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          
          .logo-icon {
            font-size: 32px;
            font-weight: bold;
            color: #F5DEB3;
            font-family: "STKaiti", serif;
          }
          
          @media (max-width: 768px) {
            width: 50px;
            height: 50px;
            margin-right: 10px;
            
            .logo-icon {
              font-size: 28px;
            }
          }
        }
        
        .site-info {
          .site-title {
            margin: 0;
            font-size: 24px;
            font-weight: bold;
            color: #F5DEB3;
            text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
            letter-spacing: 1px;
            // font-family: "STKaiti", serif;
            font-family: 'Noto Serif SC', serif;
            
            @media (max-width: 768px) {
              font-size: 20px;
              text-align: center;
              width: 100%;
            }
          }
          
          .site-subtitle {
            margin: 4px 0 0 0;
            /*font-size: 12px;*/
            font-size: 14px;
            color: #F5DEB3;
            opacity: 0.9;
            letter-spacing: 0.5px;
            
            @media (max-width: 768px) {
              font-size: 11px;
              text-align: center;
              width: 100%;
            }
          }
        }
      }
      
      .home-circle {
        position: absolute;
        /*left: calc(50% + 200px);*/ /* 向右移动位置，距离搜索框30px, 20260201 */
        /*transform: translateX(-50%);*/
        right: 695px;

        width: 60px;
        height: 60px;
        border-radius: 50%;
        border: 2px solid #F5DEB3;
        background-color: transparent;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        font-weight: bold;
        color: #F5DEB3;
        cursor: pointer;
        transition: all 0.3s ease;
        flex-shrink: 0;
        font-family: "STKaiti", serif;
        
        &:hover {
          background-color: rgba(210, 105, 30, 0.3);
          transform: translateX(-50%) scale(1.05);
        }
        
        &.active {
          background-color: rgba(210, 105, 30, 0.5);
          box-shadow: 0 0 10px rgba(210, 105, 30, 0.5);
        }
        
        @media (max-width: 768px) {
          position: absolute;
          right: 20px;
          left: auto;
          transform: none;
          top: 50%;
          transform: translateY(-50%);
          width: 50px;
          height: 50px;
          font-size: 16px;
          margin: 0;
          
          &:hover {
            transform: translateY(-50%) scale(1.05);
          }
        }
      }
      
      .search-area {
        flex: 0 0 600px;
        width: 600px;
        /*margin-right: 20px;*/ /* 距离屏幕右侧20px, 20260201*/
        margin-right: 45px;
        
        @media (max-width: 1024px) {
          flex: 0 0 400px;
          width: 400px;
        }
        
        @media (max-width: 768px) {
          flex: 1;
          order: 3;
          margin-top: 10px;
          margin-right: 0;
          width: 100%;
        }
        
        .search-box {
          display: flex;
          align-items: center;
          height: 50px;
          border: 2px solid #F5DEB3;
          border-radius: 4px;
          overflow: hidden;
          background-color: rgba(255, 255, 255, 0.1);
          width: 100%;
          
          .search-icon {
            padding: 0 12px;
            color: #F5DEB3;
            font-size: 20px;
            flex-shrink: 0;
            height: 100%;
            display: flex;
            align-items: center;
            background-color: rgba(255, 255, 255, 0.1);
          }
          
          .search-input {
            flex: 1;
            height: 100%;
            border: none;
            background-color: #ffffff;
            color: #333333;
            font-size: 15px;
            padding: 0 12px;
            outline: none;
            font-family: "Microsoft YaHei", serif;
            width: 100%;
            
            &::placeholder {
              color: rgba(51, 51, 51, 0.7);
            }
            
            &:focus {
              background-color: #ffffff;
              box-shadow: inset 0 0 5px rgba(139, 69, 19, 0.3);
            }
          }
          
          .search-button {
            height: 100%;
            padding: 0 25px;
            border: none;
            background-color: #D2691E;
            color: #F5DEB3;
            font-size: 16px;
            font-weight: bold;
            cursor: pointer;
            transition: all 0.3s;
            border-left: 2px solid #F5DEB3;
            font-family: "STKaiti", serif;
            white-space: nowrap;
            flex-shrink: 0;
            
            &:hover {
              background-color: #B3591B;
            }
            
            &:active {
              background-color: #8B4513;
            }
            
            @media (max-width: 768px) {
              padding: 0 15px;
            }
          }
        }
      }
    }
  }
  
  /* 搜索弹出层 */
  .search-results-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    z-index: 1000;
    padding-top: 88px; /* 页眉高度 */
    
    .search-results-container {
      width: 600px;
      height: calc(100vh - 88px);
      background-color: white;
      border-left: 2px solid #E78441;
      box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
      display: flex;
      flex-direction: column;
      animation: slideIn 0.3s ease;
      
      @media (max-width: 768px) {
        width: 100%;
        height: calc(100vh - 140px);
      }
      
      .search-results-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 15px 20px;
        border-bottom: 1px solid #eee;
        background-color: #fffaf5;
        flex-shrink: 0;
        
        .search-results-title {
          margin: 0;
          font-size: 18px;
          color: #5D5D5D;
          font-weight: bold;
          font-family: "STKaiti", "Microsoft YaHei", serif;
        }
        
        .search-results-close {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          border: 2px solid #E78441;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #E78441;
          background-color: white;
          transition: all 0.3s ease;
          
          &:hover {
            background-color: rgba(231, 132, 65, 0.1);
            transform: scale(1.1);
          }
          
          i {
            font-size: 14px;
            font-weight: bold;
          }
        }
      }
      
      .search-results-content {
        flex: 1;
        overflow-y: auto;
        padding: 0;
        
        .no-results {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 200px;
          color: #999;
          
          i {
            font-size: 40px;
            margin-bottom: 15px;
            color: #E78441;
          }
          
          p {
            font-size: 16px;
            font-family: "Microsoft YaHei", serif;
          }
        }
        
        .search-results-list {
          .search-result-item {
            padding: 15px 20px;
            border-bottom: 1px solid #eee;
            cursor: pointer;
            transition: all 0.2s ease;
            
            &:hover {
              background-color: #f9f9f9;
            }
            
            &:last-child {
              border-bottom: none;
            }
            
            .result-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 10px;
              
              .result-title {
                font-size: 16px;
                color: #5D5D5D;
                font-weight: bold;
                font-family: "STKaiti", "Microsoft YaHei", serif;
                flex: 1;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }
              
              .result-page {
                font-size: 14px;
                color: #E78441;
                font-weight: bold;
                background-color: #fffaf5;
                padding: 2px 8px;
                border-radius: 4px;
                border: 1px solid #E78441;
                white-space: nowrap;
                margin-left: 10px;
              }
            }
            
            .result-content {
              font-size: 14px;
              color: #666;
              line-height: 1.6;
              font-family: "SimSun", "Microsoft YaHei", serif;
              overflow: hidden;
              text-overflow: ellipsis;
              display: -webkit-box;
              -webkit-line-clamp: 3;
              -webkit-box-orient: vertical;
            }
          }
        }
      }
      
      .search-results-footer {
        padding: 10px 20px;
        border-top: 1px solid #eee;
        background-color: #fffaf5;
        text-align: center;
        font-size: 14px;
        color: #666;
        font-family: "Microsoft YaHei", serif;
        flex-shrink: 0;
      }
    }
  }
  
  /* 主内容区域 */
  .main-container {
    flex: 1;
    display: flex;
    padding: 20px;
    gap: 20px;
    box-sizing: border-box;
    height: calc(100vh - 88px);
    min-height: 0;
    overflow: hidden;
    
    /* 区域宽度控制类 */
    &.directory-collapsed {
      .directory-section {
        flex: 0 0 50px !important;
        width: 50px !important;
        min-width: 50px !important;
        
        .section-header {
          .collapse-icon {
            i {
              transform: rotate(180deg) !important;
            }
          }
        }
      }
      
      .image-section {
        flex: 1;
      }
      
      .text-section {
        flex: 1;
      }
    }
    
    &.image-collapsed {
      .image-section {
        flex: 0 0 50px !important;
        width: 50px !important;
        min-width: 50px !important;
        
        .section-header {
          .collapse-icon {
            i {
              transform: rotate(180deg) !important;
            }
          }
        }
      }
      
      .directory-section {
        flex: 0 0 calc(16.666% - 20px) !important;
      }
      
      .text-section {
        flex: 1;
      }
    }
    
    &.text-collapsed {
      .text-section {
        flex: 0 0 50px !important;
        width: 50px !important;
        min-width: 50px !important;
        
        .section-header {
          .collapse-icon {
            i {
              transform: rotate(180deg) !important;
            }
          }
        }
      }
      
      .directory-section {
        flex: 0 0 calc(16.666% - 20px) !important;
      }
      
      .image-section {
        flex: 1;
      }
    }
    
    @media (max-width: 768px) {
      flex-direction: column;
      padding: 10px;
      gap: 10px;
      height: calc(100vh - 140px - 60px); /* 减去底部导航栏高度 */
      position: relative;
      
      &.directory-collapsed,
      &.image-collapsed,
      &.text-collapsed {
        .directory-section,
        .image-section,
        .text-section {
          &.collapsed {
            display: none;
          }
        }
      }
    }
    
    /* 公共区域样式 */
    .directory-section,
    .image-section,
    .text-section {
      background-color: #ffffff;
      border: 2px solid #E78441;
      border-radius: 8px;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      transition: all 0.3s ease;
      min-height: 0;
      
      .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 20px;
        border-bottom: 1px solid #eee;
        background-color: #fffaf5;
        flex-shrink: 0;
        min-height: 36px;
        height: 36px;
        
        .section-title {
          margin: 0;
          font-size: 16px;
          color: #5D5D5D;
          font-weight: bold;
          font-family: "STKaiti", "Microsoft YaHei", serif;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          
          @media (max-width: 768px) {
            font-size: 16px; /* 移动端保持较大字体 */
          }
        }
        
        .collapse-icon {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: 2px solid #E78441;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #E78441;
          background-color: white;
          transition: all 0.3s ease;
          flex-shrink: 0;
          
          &:hover {
            background-color: rgba(231, 132, 65, 0.1);
            transform: scale(1.1);
          }
          
          i {
            font-size: 12px;
            font-weight: bold;
            transition: transform 0.3s ease;
          }
          
          @media (max-width: 768px) {
            width: 28px;
            height: 28px;
            
            i {
              font-size: 14px;
            }
          }
        }
      }
    }
    
    /* 第2区域：目录区 */
    .directory-section {
      flex: 0 0 16.666%; /* 1/6宽度 */
      width: 16.666%;
      min-width: 200px;
      max-width: 300px;
      
      &.collapsed {
        flex: 0 0 50px !important;
        width: 50px !important;
        min-width: 50px !important;
        max-width: 50px !important;
        padding: 0;
        
        .section-header {
          .section-title {
            display: none;
          }
          
          .collapse-icon {
            margin: 0 auto;
            
            i {
              transform: rotate(180deg) !important;
            }
          }
        }
        
        .expand-tabs,
        .directory-tree {
          display: none;
        }
      }
      
      @media (max-width: 768px) {
        flex: 1;
        width: 100%;
        min-width: 100%;
        max-width: 100%;
        height: 100%;
        margin-bottom: 0;
        
        &.collapsed {
          display: none;
        }
        
        &:not(.collapsed) {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 100;
        }
      }
      
      .expand-tabs {
        display: flex;
        margin: 12px 20px 0;
        border: 1px solid #E78441;
        border-radius: 6px;
        overflow: hidden;
        background-color: #fff;
        flex-shrink: 0;
        
        .expand-tab,
        .collapse-tab {
          flex: 1;
          text-align: center;
          padding: 8px 0;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: "Microsoft YaHei", serif;
        }
        
        .expand-tab {
          color: #4A4A4A;
          border-right: 1px solid #E78441;
          
          &.active {
            color: #ffffff;
            background-color: #E78441;
            font-weight: bold;
          }
          
          &:not(.active):hover {
            background-color: rgba(231, 132, 65, 0.1);
          }
        }
        
        .collapse-tab {
          color: #4A4A4A;
          
          &.active {
            color: #ffffff;
            background-color: #E78441;
            font-weight: bold;
          }
          
          &:not(.active):hover {
            background-color: rgba(231, 132, 65, 0.1);
          }
        }
        
        @media (max-width: 768px) {
          margin: 8px 15px 0;
          
          .expand-tab,
          .collapse-tab {
            padding: 10px 0;
            font-size: 14px;
          }
        }
      }
      
      .directory-tree {
        flex: 1;
        padding: 12px 20px;
        overflow-y: auto;
        min-height: 0;
        position: relative;
        
        /* 确保滚动条出现 */
        &::-webkit-scrollbar {
          width: 8px;
        }
        
        &::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        &::-webkit-scrollbar-thumb {
          background: #E78441;
          border-radius: 4px;
        }
        
        &::-webkit-scrollbar-thumb:hover {
          background: #d67338;
        }
        
        ::v-deep .book-tree {
          height: auto;
          min-height: 100%;
          
          .el-tree-node {
            .el-tree-node__content {
              height: 36px;
              border-radius: 4px;
              margin-bottom: 2px;
              transition: all 0.2s ease;
              
              &:hover {
                background-color: rgba(231, 132, 65, 0.1);
              }
              
              /* 为树节点添加标题提示样式 */
              .custom-tree-node {
                width: 100%;
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 0 5px;
                font-size: 13px;
                color: #4A4A4A;
                cursor: pointer;
                
                /* 鼠标悬停时显示完整提示 */
                &:hover {
                  .tree-node-content span {
                    text-decoration: underline;
                  }
                }
                
                .tree-node-content {
                  display: flex;
                  align-items: center;
                  flex: 1;
                  overflow: hidden;
                  
                  i {
                    margin-right: 6px;
                    color: #E78441;
                    font-size: 14px;
                    flex-shrink: 0;
                  }
                  
                  span {
                    font-family: "Microsoft YaHei", serif;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    flex: 1;
                  }
                }
                
                .page-indicator {
                  font-size: 11px;
                  color: #888;
                  background-color: #f5f5f5;
                  padding: 2px 5px;
                  border-radius: 3px;
                  border: 1px solid #eee;
                  flex-shrink: 0;
                  margin-left: 5px;
                }
              }
            }
            
            &.is-current > .el-tree-node__content {
              background-color: rgba(231, 132, 65, 0.2);
              font-weight: bold;
              
              .custom-tree-node {
                color: #E78441;
                
                .page-indicator {
                  background-color: #E78441;
                  color: white;
                  border-color: #E78441;
                }
              }
            }
          }
          
          .el-tree-node__expand-icon {
            color: #E78441;
            font-size: 14px;
            
            &.is-leaf {
              color: transparent;
            }
          }
          
          /* 确保Element UI树组件也有滚动条 */
          .el-tree__empty-block {
            min-height: 100px;
          }
          
          .el-tree-node__children {
            overflow: visible;
          }
        }
        
        @media (max-width: 768px) {
          padding: 8px 15px 20px; /* 增加底部内边距，避免被导航栏遮挡 */
          
          ::v-deep .book-tree {
            .el-tree-node {
              .el-tree-node__content {
                height: 44px; /* 移动端增加高度，便于触摸 */
                
                .custom-tree-node {
                  font-size: 14px;
                  
                  .tree-node-content {
                    i {
                      font-size: 16px;
                      margin-right: 8px;
                    }
                    
                    span {
                      font-size: 14px;
                    }
                  }
                  
                  .page-indicator {
                    font-size: 12px;
                    padding: 3px 6px;
                  }
                }
              }
            }
            
            .el-tree-node__expand-icon {
              font-size: 16px;
            }
          }
        }
      }
    }
    
    /* 第3区域：书籍原文图片区 */
    .image-section {
      flex: 1;
      min-width: 300px;
      
      &.collapsed {
        flex: 0 0 50px !important;
        width: 50px !important;
        min-width: 50px !important;
        padding: 0;
        
        .section-header {
          .section-title {
            display: none;
          }
          
          .collapse-icon {
            margin: 0 auto;
            
            i {
              transform: rotate(180deg) !important;
            }
          }
        }
        
        .image-display-area,
        .image-control-area {
          display: none;
        }
      }
      
      @media (max-width: 768px) {
        flex: 1;
        width: 100%;
        min-width: 100%;
        min-height: 300px; /* 确保在移动端有足够高度 */
        margin-bottom: 0;
        
        &.collapsed {
          display: none;
        }
        
        &:not(.collapsed) {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 100;
        }
      }
      
      .image-display-area {
        flex: 1;
        padding: 20px;
        display: flex;
        background-color: #f8f8f8;
        overflow: hidden;
        min-height: 0;
        gap: 20px;
        transition: all 0.3s ease;
        
        &.thumbnails-hidden {
          gap: 0;
          
          .image-container {
            flex: 1;
            margin: 0 auto;
            max-width: 100%;
          }
        }
        
        /* 左侧竖排小图片 */
        .thumbnail-sidebar {
          width: 80px; /* 减小宽度以适应1:1.4比例 */
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
          transition: all 0.3s ease;
          
          .thumbnail-control {
            width: 100%;
            display: flex;
            justify-content: center;
            margin-bottom: 5px;
            
            .hide-thumbnails {
              width: 30px;
              height: 30px;
              border-radius: 50%;
              border: 2px solid #E78441;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              color: #E78441;
              background-color: white;
              transition: all 0.3s ease;
              
              &:hover {
                background-color: rgba(231, 132, 65, 0.1);
                transform: scale(1.1);
              }
              
              i {
                font-size: 14px;
                font-weight: bold;
              }
            }
          }
          
          .thumbnail-nav {
            width: 100%;
            display: flex;
            justify-content: center;
            cursor: pointer;
            transition: all 0.3s ease;
            
            &.disabled {
              opacity: 0.3;
              cursor: not-allowed;
              
              .nav-circle:hover {
                transform: none;
                background-color: white;
              }
            }
            
            .nav-circle {
              width: 30px;
              height: 30px;
              border-radius: 50%;
              border: 2px solid #E78441;
              display: flex;
              align-items: center;
              justify-content: center;
              color: #E78441;
              background-color: white;
              transition: all 0.3s ease;
              
              &:hover {
                background-color: rgba(231, 132, 65, 0.1);
                transform: scale(1.1);
              }
              
              i {
                font-size: 14px;
                font-weight: bold;
              }
            }
          }
          
          .up-nav {
            margin-bottom: 5px;
          }
          
          .down-nav {
            margin-top: 5px;
          }
          
          .thumbnail-list {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 8px;
            width: 100%;
            overflow: hidden;
            
            .thumbnail-item {
              width: 80px; /* 减小宽度以适应1:1.4比例 */
              height: 112px; /* 保持高度不变，计算得出 80 * 1.4 = 112 */
              border: 2px solid #eee;
              border-radius: 4px;
              overflow: hidden;
              cursor: pointer;
              transition: all 0.3s ease;
              position: relative;
              background-color: white;
              
              &:hover {
                border-color: #E78441;
                transform: translateY(-2px);
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
              }
              
              &.active {
                border-color: #E78441;
                box-shadow: 0 0 0 2px rgba(231, 132, 65, 0.3);
              }
              
              .thumbnail-image {
                width: 100%;
                height: 100%;
                object-fit: cover;
                display: block;
              }
              
              /* 页码区域（隐藏但代码保留） */
              .thumbnail-label {
                display: none; /* 隐藏页码区域 */
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                background-color: rgba(0, 0, 0, 0.7);
                color: white;
                font-size: 10px;
                padding: 2px 4px;
                text-align: center;
                font-family: "Microsoft YaHei", serif;
              }
            }
          }
        }
        
        /* 显示小图片按钮（当小图片隐藏时显示） */
        .show-thumbnails-button {
          width: 40px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          flex-shrink: 0;
          
          .show-thumbnails {
            width: 30px;
            height: 30px;
            border-radius: 50%;
            border: 2px solid #E78441;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            color: #E78441;
            background-color: white;
            transition: all 0.3s ease;
            
            &:hover {
              background-color: rgba(231, 132, 65, 0.1);
              transform: scale(1.1);
            }
            
            i {
              font-size: 14px;
              font-weight: bold;
            }
          }
        }
        
        /* 主图片显示区 */
        .image-container {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          border-radius: 4px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          background-color: white;
          border: 1px solid #eee;
          position: relative;
          cursor: default;
          transition: all 0.3s ease;
          touch-action: manipulation; /* 移动端优化触摸交互 */
          
          .book-page-image {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
            display: block;
            -webkit-user-drag: none; /* 防止移动端拖动图片 */
            user-select: none; /* 防止移动端选中图片 */
          }
          
          .drag-hint {
            position: absolute;
            bottom: 10px;
            right: 10px;
            background-color: rgba(0, 0, 0, 0.7);
            color: white;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-family: "Microsoft YaHei", serif;
            z-index: 10;
          }
        }
        
        @media (max-width: 768px) {
          padding: 10px 10px 15px; /* 增加底部内边距，避免被导航栏遮挡 */
          flex-direction: column;
          gap: 10px;
          
          &.thumbnails-hidden {
            gap: 0;
            
            .show-thumbnails-button {
              width: 100%;
              flex-direction: row;
              justify-content: center;
              margin-bottom: 10px;
              
              .show-thumbnails {
                transform: rotate(90deg);
              }
            }
            
            .image-container {
              width: 100%;
              height: 60vh; /* 移动端图片区域占60%屏幕高度 */
              min-height: 300px;
              margin: 0 auto;
            }
          }
          
          .thumbnail-sidebar {
            width: 100% !important;
            flex-direction: row;
            justify-content: center;
            gap: 5px;
            margin-bottom: 10px;
            order: 2;
            
            .thumbnail-control {
              width: auto;
              margin-bottom: 0;
              margin-right: 5px;
              
              .hide-thumbnails {
                transform: rotate(45deg);
              }
            }
            
            .thumbnail-nav {
              width: auto;
              
              &.up-nav {
                margin-bottom: 0;
                margin-right: 5px;
              }
              
              &.down-nav {
                margin-top: 0;
                margin-left: 5px;
              }
            }
            
            .thumbnail-list {
              flex-direction: row;
              flex: 1;
              justify-content: center;
              gap: 5px;
              
              .thumbnail-item {
                width: 50px; /* 移动端宽度调整为50px */
                height: 70px; /* 保持1:1.4宽高比 50 * 1.4 = 70 */
              }
            }
          }
          
          .show-thumbnails-button {
            width: 100%;
            flex-direction: row;
            justify-content: center;
            margin-bottom: 10px;
            
            .show-thumbnails {
              transform: rotate(90deg);
            }
          }
          
          .image-container {
            width: 100%;
            height: 60vh; /* 移动端图片区域占60%屏幕高度 */
            min-height: 300px;
          }
        }
        
        @media (max-width: 480px) {
          .thumbnail-sidebar {
            .thumbnail-list {
              .thumbnail-item {
                width: 45px; /* 小屏幕宽度调整为45px */
                height: 63px; /* 保持1:1.4宽高比 45 * 1.4 = 63 */
              }
            }
          }
        }
      }
      
      .image-control-area {
        padding: 12px 20px 20px; /* 增加底部内边距，避免被导航栏遮挡 */
        border-top: 1px solid #eee;
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: #fffaf5;
        flex-wrap: wrap;
        gap: 12px;
        flex-shrink: 0;
        
        .pagination-control,
        .zoom-control,
        .original-control {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .control-button {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 2px solid #E78441;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #E78441;
          background-color: white;
          transition: all 0.2s ease;
          flex-shrink: 0;
          
          &:hover:not(.disabled) {
            background-color: rgba(231, 132, 65, 0.1);
            transform: scale(1.05);
          }
          
          &.disabled {
            opacity: 0.5;
            cursor: not-allowed;
            border-color: #ccc;
            color: #ccc;
          }
          
          i {
            font-size: 14px;
            font-weight: bold;
          }
        }
        
        .page-info {
          font-size: 14px;
          color: #5D5D5D;
          font-weight: bold;
          min-width: 70px;
          text-align: center;
          font-family: "STKaiti", serif;
          
          .separator {
            margin: 0 4px;
            color: #999;
          }
        }
        
        .page-jump {
          display: flex;
          align-items: center;
          gap: 4px;
          
          .jump-input {
            width: 55px;
            height: 32px;
            border: 1px solid #E78441;
            border-radius: 4px;
            padding: 0 6px;
            text-align: center;
            font-size: 13px;
            color: #5D5D5D;
            
            &:focus {
              outline: none;
              border-color: #D2691E;
              box-shadow: 0 0 0 2px rgba(231, 132, 65, 0.2);
            }
          }
          
          .jump-button {
            height: 32px;
            padding: 0 10px;
            border: 1px solid #E78441;
            border-radius: 4px;
            background-color: #E78441;
            color: white;
            font-size: 13px;
            cursor: pointer;
            transition: all 0.2s ease;
            font-family: "Microsoft YaHei", serif;
            
            &:hover {
              background-color: #d67338;
              border-color: #d67338;
            }
          }
        }
        
        .zoom-info {
          font-size: 14px;
          color: #5D5D5D;
          font-weight: bold;
          min-width: 55px;
          text-align: center;
        }
        
        .original-control {
          .original-text {
            font-size: 14px;
            color: #5D5D5D;
            font-weight: bold;
            margin-right: 8px;
            font-family: "STKaiti", serif;
          }
        }
        
        @media (max-width: 768px) {
          padding: 10px 15px 20px; /* 增加底部内边距，避免被导航栏遮挡 */
          flex-direction: column;
          align-items: stretch;
          gap: 15px; /* 增加间距，避免控件拥挤 */
          
          .pagination-control,
          .zoom-control,
          .original-control {
            justify-content: center;
            width: 100%;
            flex-wrap: wrap;
            margin-bottom: 5px; /* 增加控件之间的间距 */
          }
          
          .control-button {
            width: 40px; /* 增加按钮大小 */
            height: 40px;
            
            i {
              font-size: 18px; /* 增加图标大小 */
            }
          }
          
          .page-info {
            font-size: 16px;
            min-width: 80px;
            margin: 0 10px; /* 增加间距 */
          }
          
          .page-jump {
            .jump-input {
              width: 60px;
              height: 36px;
              font-size: 14px;
            }
            
            .jump-button {
              height: 36px;
              padding: 0 12px;
              font-size: 14px;
            }
          }
          
          .zoom-info {
            font-size: 16px;
            min-width: 60px;
            margin: 0 10px; /* 增加间距 */
          }
          
          .original-control {
            .original-text {
              font-size: 16px;
              margin-right: 10px;
            }
          }
        }
        
        @media (max-width: 480px) {
          .control-button {
            width: 36px;
            height: 36px;
            
            i {
              font-size: 16px;
            }
          }
          
          .page-info {
            font-size: 15px;
            min-width: 70px;
          }
          
          .zoom-info {
            font-size: 15px;
            min-width: 55px;
          }
        }
      }
    }
    
    /* 第4区域：书籍文字区 */
    .text-section {
      flex: 1;
      min-width: 300px;
      
      &.collapsed {
        flex: 0 0 50px !important;
        width: 50px !important;
        min-width: 50px !important;
        padding: 0;
        
        .section-header {
          .section-title {
            display: none;
          }
          
          .collapse-icon {
            margin: 0 auto;
            
            i {
              transform: rotate(180deg) !important;
            }
          }
        }
        
        .text-content-area {
          display: none;
        }
      }
      
      @media (max-width: 768px) {
        flex: 1;
        width: 100%;
        min-width: 100%;
        min-height: 300px; /* 确保在移动端有足够高度 */
        margin-bottom: 0;
        
        &.collapsed {
          display: none;
        }
        
        &:not(.collapsed) {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 100;
        }
      }
      
      .text-content-area {
        flex: 1;
        padding: 20px 20px 30px; /* 增加底部内边距，避免被导航栏遮挡 */
        overflow-y: auto;
        background-color: #fff;
        min-height: 0;
        
        .chapter-content {
          .section-subtitle {
            color: #E78441;
            border-bottom: 1px solid #eee;
            padding-bottom: 8px;
            margin-top: 0;
            font-size: 18px;
            font-weight: bold;
            font-family: "STKaiti", "Microsoft YaHei", serif;
            
            @media (max-width: 768px) {
              font-size: 18px; /* 移动端保持较大字体 */
              padding-bottom: 6px;
            }
          }
          
          .book-text-content {
            color: #4A4A4A;
            line-height: 1.8;
            font-size: 15px;
            font-family: "SimSun", "Microsoft YaHei", serif;
            
            p {
              text-indent: 2em;
              margin-bottom: 1em;
              text-align: justify;
              
              &:first-child {
                margin-top: 0;
              }
            }
            
            @media (max-width: 768px) {
              font-size: 16px; /* 移动端增加字体大小 */
              line-height: 1.9;
              
              p {
                margin-bottom: 1.2em;
                text-indent: 2.2em;
                padding-bottom: 5px; /* 增加段落底部间距 */
              }
            }
          }
        }
        
        @media (max-width: 768px) {
          padding: 15px 15px 25px; /* 增加移动端底部内边距 */
        }
      }
    }
  }
  
  /* 移动端导航 */
  .mobile-navigation {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 60px;
    background-color: #8B4513;
    display: flex;
    justify-content: space-around;
    padding: 8px 0;
    border-top: 2px solid #D2691E;
    z-index: 1000;
    display: none;
    
    @media (max-width: 768px) {
      display: flex;
    }
    
    .mobile-nav-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #F5DEB3;
      cursor: pointer;
      transition: all 0.3s ease;
      padding: 5px 10px;
      border-radius: 4px;
      flex: 1;
      -webkit-tap-highlight-color: transparent; /* 移除移动端点击高亮 */
      
      i {
        font-size: 20px;
        margin-bottom: 4px;
      }
      
      span {
        font-size: 12px;
        font-family: "Microsoft YaHei", serif;
      }
      
      &.active {
        background-color: rgba(210, 105, 30, 0.3);
        
        i, span {
          color: #FFD700;
        }
      }
      
      &:active {
        background-color: rgba(210, 105, 30, 0.2);
        transform: scale(0.95);
      }
    }
    
    @media (max-width: 480px) {
      height: 55px;
      padding: 6px 0;
      
      .mobile-nav-item {
        padding: 4px 8px;
        
        i {
          font-size: 18px;
          margin-bottom: 3px;
        }
        
        span {
          font-size: 11px;
        }
      }
    }
  }
  
  /* 搜索弹出层动画 */
  @keyframes slideIn {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }
}

/* 响应式调整 */
@media (min-width: 769px) and (max-width: 1024px) {
  .main-container {
    padding: 15px;
    gap: 15px;
  }
  
  .header-container {
    padding: 0 15px;
  }
  
  .logo-area {
    min-width: 250px !important;
  }
  
  .search-area {
    flex: 0 0 400px !important;
  }
  
  .search-results-overlay {
    .search-results-container {
      width: 500px;
    }
  }
  
  /* 平板端调整小图片区域 */
  .image-display-area {
    .thumbnail-sidebar {
      width: 70px !important; /* 平板端调整为70px */
      
      .thumbnail-list {
        .thumbnail-item {
          width: 70px !important; /* 与父容器宽度一致 */
          height: 98px !important; /* 保持1:1.4宽高比 70 * 1.4 = 98 */
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .header {
    height: auto !important;
  }
  
  .header-container {
    padding: 10px 15px !important;
    flex-direction: column;
    align-items: stretch;
    
    .logo-area {
      min-width: auto;
      flex: 1;
      justify-content: center;
      flex-direction: column;
      text-align: center;
      margin-bottom: 10px;
      
      .logo {
        margin: 0 auto 10px;
      }
    }
    
    .home-circle {
      position: absolute;
      right: 15px;
      top: 20px;
      left: auto;
      transform: none;
      margin: 0;
      width: 50px;
      height: 50px;
    }
    
    .search-area {
      margin-right: 0 !important;
      width: 100% !important;
      order: 2;
    }
  }
  
  .search-results-overlay {
    padding-top: 140px;
    
    .search-results-container {
      width: 100%;
      height: calc(100vh - 140px - 60px); /* 减去底部导航栏高度 */
    }
  }
}

// 20260129,此处去掉了原来设定的全局css的设置，解决了该界面css全局影响其他界面，导致工程发布到nginx后，手机上的界面不能上下滚动的问题
.search-message {
  background-color: #8B4513 !important;
  border-color: #D2691E !important;
  color: #F5DEB3 !important;
  
  .el-message__content {
    color: #F5DEB3;
  }
}

.home-message {
  background-color: #E78441 !important;
  border-color: #D2691E !important;
  color: #fff !important;
}

/* 移动端优化触摸滚动 */
@media (max-width: 768px) {
  * {
    -webkit-tap-highlight-color: transparent;
  }
  
  body {
    overflow: hidden;
  }
  
  input, textarea, button, select {
    font-size: 16px; /* 防止iOS输入框缩放 */
  }
}

/* 滚动条样式 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #E78441;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #d67338;
}

/* Element UI 树组件自定义样式 */
.el-tree {
  background-color: transparent;
  
  .el-tree-node__content:hover {
    background-color: rgba(231, 132, 65, 0.1) !important;
  }
  
  .el-tree-node:focus > .el-tree-node__content {
    background-color: rgba(231, 132, 65, 0.2) !important;
  }
  
  .el-tree-node.is-current > .el-tree-node__content {
    background-color: rgba(231, 132, 65, 0.2) !important;
    color: #E78441 !important;
  }
  
  /* 目录树节点悬停提示样式 */
  .el-tree-node__content {
    .custom-tree-node {
      .tree-node-content span {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }
    }
  }
  
  /* 移动端适配 */
  @media (max-width: 768px) {
    .el-tree-node__content {
      min-height: 44px;
    }
  }
}

/* 关键词高亮样式 */
.highlight {
  color: #ff0000;
  background-color: rgba(255, 0, 0, 0.1);
  font-weight: bold;
  padding: 0 2px;
  border-radius: 2px;
  border: 1px solid rgba(255, 0, 0, 0.2);
}
</style>


// WEBPACK FOOTER //
// src/views/vipread/index.vue